import React, { useEffect } from 'react';
import { Cell, Pie, PieChart } from 'recharts';
import { WidgetWrapper } from '../widgets/common/styles';
import SkeletonLoader from '../../skeletonLoaders/Loader';
import { WidgetLabels, WidgetValues } from './types';
import { useTranslation } from 'react-i18next';
import { Flex } from 'lib/components/styles/layout';
import { WidgetTypeTooltip } from './common/WidgetTypeTooltip';
import { useQueryMyTeamStats } from 'lib/api/metrics/useQueryMyTeamStats';
import { Typography } from 'react-covideo-common';

const MyTeamStats = ({ params, addDataForExport }: any) => {
  const { t } = useTranslation();
  const pies = [
    { id: 'videos', label: t('recorded videos') },
    { id: 'sentEmails', label: t('sent') },
    { id: 'views', label: t('total viewed') },
  ];

  const {
    data: myTeamStatsData,
    isLoading,
    isRefetching,
  } = useQueryMyTeamStats({
    params,
    enabled: true,
  });

  useEffect(() => {
    if (!myTeamStatsData) return;

    const xmlData = {
      title: WidgetLabels.MY_TEAM_STATS,
      data: [
        {
          Recorded: myTeamStatsData.videos,
          Sent: myTeamStatsData.sentEmails,
          'Total Viewed': myTeamStatsData.views,
        },
      ],
    };

    addDataForExport(xmlData);
  }, [myTeamStatsData]);

  if (isLoading) {
    return <SkeletonLoader imageName='three-circles' />;
  }

  const transformData: Record<string, number> = {
    videos: myTeamStatsData?.videos ?? 0,
    sentEmails: myTeamStatsData?.sentEmails ?? 0,
    views: myTeamStatsData?.views ?? 0,
  };

  return (
    <WidgetWrapper>
      <Flex
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='normal' fontWeight={600}>
          {t(WidgetLabels.MY_TEAM_STATS)}
          {isRefetching && <span> {t('Updating...')}</span>}
        </Typography>
        <WidgetTypeTooltip widgetType={WidgetValues.MY_TEAM_STATS} />
      </Flex>
      <Flex justifyContent='space-between' flexDirection='row' gap='32px'>
        {pies.map(({ id, label }) => {
          const value: number = transformData?.[id];
          const total = transformData?.['videos'];

          const pieData = [
            {
              name: 'total',
              value: total === 0 ? 1 : id === 'videos' ? 0 : total,
            },
            {
              name: 'unused',
              value: value,
            },
          ];

          return (
            <Flex alignItems='center' style={{ position: 'relative' }} key={id}>
              <Typography variant='normal' fontWeight={600}>
                {value}
              </Typography>
              <PieChart
                width={110}
                height={110}
                style={{ transform: 'rotate(270deg)' }}
              >
                <Pie
                  direction=''
                  data={pieData}
                  innerRadius={30}
                  outerRadius={45}
                  fill='#8884d8'
                  paddingAngle={0}
                  dataKey='value'
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.name !== 'total'
                          ? 'rgba(0, 27, 83, 1)'
                          : 'rgba(0, 27, 83, 0.2)'
                      }
                    />
                  ))}
                </Pie>
              </PieChart>
              <Typography variant='small'>{label}</Typography>
            </Flex>
          );
        })}
      </Flex>
    </WidgetWrapper>
  );
};

export default MyTeamStats;
